import bs58 from "bs58";
import { z } from "zod/v4";
import { SOLANA_CHAIN_REFERENCE, Validator } from "@repo/caip";
import {
  HYPEREVM_CHAIN_ID,
  MONAD_CHAIN_ID,
  ROBINHOOD_CHAIN_ID,
} from "@repo/constants";
import {
  arbitrum,
  base,
  blast,
  bsc,
  linea,
  mainnet,
  optimism,
  polygon,
  unichain,
} from "viem/chains";

export type RuntimeEnvRecord = Record<string, string | undefined>;

export type RpcTransport = "HTTPS" | "WSS";

export type Eip155RpcRole = "BUNDLER" | "PAYMASTER" | "ULTRA_RELAY";

export const getRuntimeEnvRecord = (): RuntimeEnvRecord => {
  const globalWithProcess = globalThis as typeof globalThis & {
    process?: {
      env?: RuntimeEnvRecord;
    };
  };

  return globalWithProcess.process?.env ?? {};
};

const normalizeEnvChainId = (chainId: number | string): string => {
  const chainIdString =
    typeof chainId === "number" ? chainId.toString(10) : chainId.trim();
  if (!/^[1-9]\d*$/.test(chainIdString)) {
    throw new Error(
      `Invalid chain id '${chainIdString}' for RPC env name. Expected positive integer chain id.`,
    );
  }
  return chainIdString;
};

export const eip155RpcEnvName = (
  chainId: number | string,
  transport: RpcTransport,
): string => `RPC_EIP155_${normalizeEnvChainId(chainId)}_${transport}`;

export const eip155RoleRpcEnvName = (
  chainId: number | string,
  role: Eip155RpcRole,
  transport: RpcTransport,
): string => `RPC_EIP155_${normalizeEnvChainId(chainId)}_${role}_${transport}`;

export const solanaRpcEnvName = (transport: RpcTransport): string =>
  `RPC_SOLANA_${transport}`;

export const suiRpcEnvName = (transport: RpcTransport): string =>
  `RPC_SUI_${transport}`;

export const solanaMainnetRpcEnvName = (transport: RpcTransport): string =>
  `RPC_SOLANA_MAINNET_${transport}`;

export const readFirstRuntimeEnvValue = (
  env: RuntimeEnvRecord,
  names: readonly string[],
): string | undefined => {
  for (const name of names) {
    const value = env[name]?.trim();
    if (value) {
      return value;
    }
  }
  return undefined;
};

const parseBooleanEnvValue = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }

  if (normalized === "true") {
    return true;
  }

  if (normalized === "false") {
    return false;
  }

  return undefined;
};

export const booleanEnvWithDefault = (defaultValue: boolean) =>
  z.preprocess((value) => {
    if (value === undefined || value === null) {
      return defaultValue;
    }

    if (typeof value === "string" && value.trim() === "") {
      return defaultValue;
    }

    return parseBooleanEnvValue(value) ?? value;
  }, z.boolean());

/** Reusable Zod schema for boolean-from-string env vars (e.g. IS_STAGING). */
export const booleanEnvSchema = booleanEnvWithDefault(false);

/**
 * Decode-verified base58 Solana pubkey check. A Solana pubkey is always
 * exactly 32 bytes, which base58-encodes to a 32–44-character string
 * over the alphabet `[1-9A-HJ-NP-Za-km-z]`. The naive length-band
 * regex catches obvious typos but happily accepts a 32-char string
 * like `"11111111111111111111111111111111"` that decodes to ~23 bytes
 * and breaks `new PublicKey()` at the consumer side.
 *
 * Centralized here so both engine and platform-api env validation
 * share one implementation; a future tweak to the validation
 * (allowing whitespace, lowercasing, etc.) lands in one place.
 *
 * Returns `true` only when the input decodes to exactly 32 bytes.
 */
export function isSolanaBase58Pubkey(value: string): boolean {
  if (
    typeof value !== "string" ||
    value.length < 32 ||
    value.length > 44 ||
    !/^[1-9A-HJ-NP-Za-km-z]+$/.test(value)
  ) {
    return false;
  }
  try {
    return bs58.decode(value).length === 32;
  } catch {
    return false;
  }
}

/**
 * Zod refinement convenience wrapper. Use as:
 *   `z.string().refine(...solanaBase58PubkeyRefinement("MY_VAR"))`
 * to keep the error message uniform across env declarations.
 */
export function solanaBase58PubkeyRefinement(
  varName: string,
): [(value: string) => boolean, { message: string }] {
  return [
    isSolanaBase58Pubkey,
    {
      message:
        `${varName} must be a base58-encoded Solana pubkey (decodes to ` +
        `exactly 32 bytes). A typo here is silent until first use — fail ` +
        `loudly at config load instead.`,
    },
  ];
}

export const EVM_INFRA_SUPPORTED_CHAIN_IDS = Object.freeze([
  polygon.id,
  base.id,
  arbitrum.id,
  optimism.id,
  blast.id,
  mainnet.id,
  linea.id,
  unichain.id,
  bsc.id,
  ROBINHOOD_CHAIN_ID,
  HYPEREVM_CHAIN_ID,
  MONAD_CHAIN_ID,
]);
export const EVM_INFRA_SUPPORTED_CHAIN_ID_STRINGS = Object.freeze(
  EVM_INFRA_SUPPORTED_CHAIN_IDS.map((chainId) => chainId.toString(10)),
);
const EVM_INFRA_SUPPORTED_CHAIN_ID_SET = new Set<number>(
  EVM_INFRA_SUPPORTED_CHAIN_IDS,
);

export const DEFAULT_RUNTIME_ACTIVE_EVM_CHAIN_IDS = Object.freeze([
  base.id,
  mainnet.id,
  arbitrum.id,
]);

const isValidHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const parseRuntimeChainToken = (token: string): number | null => {
  if (!/^\d+$/.test(token)) {
    return null;
  }
  const parsed = Number.parseInt(token, 10);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
};

/**
 * Parse runtime-active EVM chain IDs from CSV.
 * Defaults to Base + Ethereum mainnet + Arbitrum when unset.
 */
export const parseRuntimeActiveEvmChainIds = (
  runtimeActiveChainIdsRaw: string | undefined,
): number[] => {
  if (
    runtimeActiveChainIdsRaw === undefined ||
    runtimeActiveChainIdsRaw.trim().length === 0
  ) {
    return [...DEFAULT_RUNTIME_ACTIVE_EVM_CHAIN_IDS];
  }

  const seen = new Set<number>();
  const chainIds: number[] = [];

  for (const token of runtimeActiveChainIdsRaw.split(",")) {
    const trimmedToken = token.trim();
    if (!trimmedToken) {
      continue;
    }

    const parsedChainId = parseRuntimeChainToken(trimmedToken);
    if (parsedChainId === null) {
      throw new Error(
        `Invalid chain id '${trimmedToken}' in RUNTIME_ACTIVE_EVM_CHAIN_IDS. Expected positive integer chain ids.`,
      );
    }
    if (!EVM_INFRA_SUPPORTED_CHAIN_ID_SET.has(parsedChainId)) {
      throw new Error(
        `Unsupported chain id '${parsedChainId}' in RUNTIME_ACTIVE_EVM_CHAIN_IDS. Supported values: ${EVM_INFRA_SUPPORTED_CHAIN_ID_STRINGS.join(",")}`,
      );
    }
    if (seen.has(parsedChainId)) {
      continue;
    }
    seen.add(parsedChainId);
    chainIds.push(parsedChainId);
  }

  if (chainIds.length === 0) {
    throw new Error(
      "RUNTIME_ACTIVE_EVM_CHAIN_IDS resolved to an empty set. Provide at least one chain id.",
    );
  }

  return chainIds;
};

export const resolveRuntimeActiveEvmChainIdsFromEnv = (
  env: RuntimeEnvRecord = getRuntimeEnvRecord(),
): number[] => parseRuntimeActiveEvmChainIds(env.RUNTIME_ACTIVE_EVM_CHAIN_IDS);

export const SOLANA_MAINNET_CHAIN_REF =
  `solana:${SOLANA_CHAIN_REFERENCE}` as const;
export const SUI_MAINNET_CHAIN_REF = "sui:mainnet" as const;

export type ActiveChainRefsEnvName =
  | "ACTIVE_SVM_CHAIN_REFS"
  | "ACTIVE_SUI_CHAIN_REFS";
export type ActiveChainRefsParseErrorReason = "invalid" | "unsupported";

export class ActiveChainRefsParseError extends Error {
  constructor(
    message: string,
    public readonly envName: ActiveChainRefsEnvName,
    public readonly entry: string,
    public readonly reason: ActiveChainRefsParseErrorReason,
  ) {
    super(message);
    this.name = "ActiveChainRefsParseError";
  }
}

const parseRuntimeActiveSingletonChainRefs = <SupportedChainRef extends string>(
  raw: string | undefined,
  params: {
    envName: ActiveChainRefsEnvName;
    namespace: "solana" | "sui";
    networkName: "Solana" | "Sui";
    supportedChainRef: SupportedChainRef;
  },
): SupportedChainRef[] => {
  const chainRefs: SupportedChainRef[] = [];

  for (const value of (raw ?? "").split(",")) {
    const chainRef = value.trim();
    if (!chainRef) {
      continue;
    }

    const separatorIndex = chainRef.indexOf(":");
    const namespace = chainRef.slice(0, separatorIndex);
    const reference = chainRef.slice(separatorIndex + 1);
    const isCanonicalForNamespace =
      separatorIndex > 0 &&
      chainRef.indexOf(":", separatorIndex + 1) === -1 &&
      namespace === params.namespace &&
      Validator.isValidChainReference(reference) &&
      chainRef === `${namespace}:${reference}`;

    if (!isCanonicalForNamespace) {
      throw new ActiveChainRefsParseError(
        `Invalid ${params.envName} entry '${chainRef}': expected a canonical CAIP-2 ${params.networkName} chain id.`,
        params.envName,
        chainRef,
        "invalid",
      );
    }

    if (chainRef !== params.supportedChainRef) {
      throw new ActiveChainRefsParseError(
        `Unsupported ${params.envName} entry '${chainRef}': only ${params.networkName} mainnet (${params.supportedChainRef}) is supported.`,
        params.envName,
        chainRef,
        "unsupported",
      );
    }

    chainRefs.push(params.supportedChainRef);
  }

  return chainRefs;
};

export const parseRuntimeActiveSvmChainRefs = (
  raw: string | undefined,
): Array<typeof SOLANA_MAINNET_CHAIN_REF> =>
  parseRuntimeActiveSingletonChainRefs(raw, {
    envName: "ACTIVE_SVM_CHAIN_REFS",
    namespace: "solana",
    networkName: "Solana",
    supportedChainRef: SOLANA_MAINNET_CHAIN_REF,
  });

export const resolveRuntimeActiveSvmChainRefsFromEnv = (
  env: RuntimeEnvRecord,
): Array<typeof SOLANA_MAINNET_CHAIN_REF> =>
  parseRuntimeActiveSvmChainRefs(env.ACTIVE_SVM_CHAIN_REFS);

export const parseRuntimeActiveSuiChainRefs = (
  raw: string | undefined,
): Array<typeof SUI_MAINNET_CHAIN_REF> =>
  parseRuntimeActiveSingletonChainRefs(raw, {
    envName: "ACTIVE_SUI_CHAIN_REFS",
    namespace: "sui",
    networkName: "Sui",
    supportedChainRef: SUI_MAINNET_CHAIN_REF,
  });

export const resolveRuntimeActiveSuiChainRefsFromEnv = (
  env: RuntimeEnvRecord,
): Array<typeof SUI_MAINNET_CHAIN_REF> =>
  parseRuntimeActiveSuiChainRefs(env.ACTIVE_SUI_CHAIN_REFS);

export type EvmInfraEnvValidationIssueReason = "missing" | "invalid_url";

export interface EvmInfraEnvValidationIssue {
  chainId: string;
  envVar: string;
  message: string;
  reason: EvmInfraEnvValidationIssueReason;
}

/**
 * Validate that required EVM infra env vars are present for the given chain set.
 * Base may use the Coinbase endpoint as both its ERC-7677 paymaster and
 * bundler. Engine callers may explicitly allow a Pimlico API key to provide
 * the same coverage.
 */
export const validateEvmInfraEnvCoverageForChains = (params: {
  chainIds: ReadonlyArray<number | string>;
  env?: RuntimeEnvRecord;
  pimlicoApiKeyProvidesAaCoverage?: boolean;
}): EvmInfraEnvValidationIssue[] => {
  const env = params.env ?? getRuntimeEnvRecord();
  const issues: EvmInfraEnvValidationIssue[] = [];

  // TODO(svm): validate Solana RPC coverage once ACTIVE_SVM_CHAIN_REFS is enabled in a deployment.
  for (const chainId of params.chainIds) {
    const chainIdString = normalizeEnvChainId(chainId);
    const jsonRpcKey = eip155RpcEnvName(chainIdString, "HTTPS");
    const legacyJsonRpcKey = `JSON_RPC_URL_${chainIdString}`;
    const paymasterKey = eip155RoleRpcEnvName(
      chainIdString,
      "PAYMASTER",
      "HTTPS",
    );
    const legacyPaymasterKey = `PAYMASTER_RPC_URL_${chainIdString}`;
    const bundlerKey = eip155RoleRpcEnvName(chainIdString, "BUNDLER", "HTTPS");
    const legacyBundlerKey = `BUNDLER_RPC_URL_${chainIdString}`;
    const ultraRelayKey = eip155RoleRpcEnvName(
      chainIdString,
      "ULTRA_RELAY",
      "HTTPS",
    );
    const legacyUltraRelayKey = `ULTRA_RELAY_RPC_URL_${chainIdString}`;
    const coinbaseBundlerKey = `COINBASE_BUNDLER_RPC_URL_${chainIdString}`;
    const providerOverrideKey = `ENGINE_ERC4337_PROVIDER_${chainIdString}`;
    const providerOverride = readFirstRuntimeEnvValue(env, [
      providerOverrideKey,
    ])?.toLowerCase();
    const pimlicoApiKey = readFirstRuntimeEnvValue(env, ["PIMLICO_API_KEY"]);
    const pimlicoProviderSelected =
      params.pimlicoApiKeyProvidesAaCoverage === true &&
      (providerOverride === "pimlico" ||
        (chainIdString === base.id.toString(10) &&
          providerOverride === undefined &&
          pimlicoApiKey !== undefined));
    const hasPimlicoCoverage =
      pimlicoProviderSelected && pimlicoApiKey !== undefined;

    if (pimlicoProviderSelected && !pimlicoApiKey) {
      issues.push({
        chainId: chainIdString,
        envVar: "PIMLICO_API_KEY",
        reason: "missing",
        message: `PIMLICO_API_KEY is required when ${providerOverrideKey} selects Pimlico.`,
      });
    }
    const coinbaseBundlerUrl =
      chainIdString === base.id.toString(10)
        ? readFirstRuntimeEnvValue(env, [coinbaseBundlerKey])
        : undefined;
    const hasValidCoinbaseBundlerUrl =
      coinbaseBundlerUrl !== undefined && isValidHttpUrl(coinbaseBundlerUrl);

    if (coinbaseBundlerUrl && !hasValidCoinbaseBundlerUrl) {
      issues.push({
        chainId: chainIdString,
        envVar: coinbaseBundlerKey,
        reason: "invalid_url",
        message: `${coinbaseBundlerKey} must be a valid http(s) URL.`,
      });
    }

    const jsonRpcUrl = readFirstRuntimeEnvValue(env, [
      jsonRpcKey,
      legacyJsonRpcKey,
    ]);
    if (!jsonRpcUrl) {
      issues.push({
        chainId: chainIdString,
        envVar: jsonRpcKey,
        reason: "missing",
        message: `${jsonRpcKey} is required for runtime-active chain ${chainIdString}.`,
      });
    } else if (!isValidHttpUrl(jsonRpcUrl)) {
      issues.push({
        chainId: chainIdString,
        envVar: jsonRpcKey,
        reason: "invalid_url",
        message: `${jsonRpcKey} must be a valid http(s) URL.`,
      });
    }

    const paymasterUrl = readFirstRuntimeEnvValue(env, [
      paymasterKey,
      legacyPaymasterKey,
    ]);
    if (
      !paymasterUrl &&
      !hasValidCoinbaseBundlerUrl &&
      !hasPimlicoCoverage &&
      !pimlicoProviderSelected
    ) {
      issues.push({
        chainId: chainIdString,
        envVar: paymasterKey,
        reason: "missing",
        message: `${paymasterKey} is required for runtime-active chain ${chainIdString}.`,
      });
    } else if (paymasterUrl && !isValidHttpUrl(paymasterUrl)) {
      issues.push({
        chainId: chainIdString,
        envVar: paymasterKey,
        reason: "invalid_url",
        message: `${paymasterKey} must be a valid http(s) URL.`,
      });
    }

    const bundlerUrl = readFirstRuntimeEnvValue(env, [
      bundlerKey,
      legacyBundlerKey,
    ]);
    const ultraRelayUrl = readFirstRuntimeEnvValue(env, [
      ultraRelayKey,
      legacyUltraRelayKey,
    ]);

    if (
      !bundlerUrl &&
      !ultraRelayUrl &&
      !hasValidCoinbaseBundlerUrl &&
      !hasPimlicoCoverage &&
      !pimlicoProviderSelected
    ) {
      issues.push({
        chainId: chainIdString,
        envVar: bundlerKey,
        reason: "missing",
        message: `Either ${bundlerKey}, ${ultraRelayKey}, ${coinbaseBundlerKey}, or direct Pimlico via PIMLICO_API_KEY is required for runtime-active chain ${chainIdString}.`,
      });
      continue;
    }

    if (bundlerUrl && !isValidHttpUrl(bundlerUrl)) {
      issues.push({
        chainId: chainIdString,
        envVar: bundlerKey,
        reason: "invalid_url",
        message: `${bundlerKey} must be a valid http(s) URL.`,
      });
    }

    if (ultraRelayUrl && !isValidHttpUrl(ultraRelayUrl)) {
      issues.push({
        chainId: chainIdString,
        envVar: ultraRelayKey,
        reason: "invalid_url",
        message: `${ultraRelayKey} must be a valid http(s) URL.`,
      });
    }
  }

  return issues;
};
